// ownerSyncClient — staff (Bearer) calls for the AppFolio owner / vendor sync
// (docs/appfolio-owner-vendor-sync-spec.md §12):
//
//   GET  /api/owners/lookup?email=            → { matches: OwnerMatch[] }
//   GET  /api/owners/search?q=                → { owners: OwnerMatch[] }
//   GET  /api/vendors/search?q=               → { vendors: VendorMatch[] }
//   POST /api/w9-requests                     → 201 | 409 | 400 | 503
//   GET  /api/owner-sync/:engagementGroupId   → { ops, appfolioOwnerLinked }
//   POST /api/owner-sync/ops/retry            { opKey }
//   POST /api/owner-sync/ops/resolve          { opKey, appfolioId?, note? }
//   POST /api/owner-sync/ops/resend-confirmed { opKey }
//
// The lookups are an ASSIST, never a gate: any failure (the endpoint not yet
// deployed, a 503, a network blip) reads as "no matches", so the EPMA form and
// the pickers degrade to exactly what they were before this feature.
//
// Wire shapes and the pure display logic live in lib/ownerSync.ts (re-exported).

import { api, ApiError } from './api';
import { OPAQUE_ID_RE } from './noIds';
import type {
  LinkPropertyMismatch,
  OwnerMatch,
  OwnerSyncView,
  SimilarOwner,
  VendorMatch,
  W9RequestBody,
  W9RequestOutcome,
} from './ownerSync';
import { linkPropertyMismatchFrom, ownerMatchAvailableFrom } from './ownerSync';

export type {
  AppFolioSyncStatus,
  LinkablePropertyOption,
  LinkPropertyMismatch,
  OwnerChainPlan,
  OwnerChainStep,
  OwnerMatch,
  OwnerSyncOpKind,
  OwnerSyncOpView,
  OwnerSyncView,
  SimilarOwner,
  VendorMatch,
  W9RecipientKind,
  W9RequestBody,
  W9RequestOutcome,
} from './ownerSync';

/** Existing AppFolio owners with this email (case-insensitive). [] on any failure. */
export async function lookupOwnersByEmail(email: string): Promise<OwnerMatch[]> {
  const e = email.trim();
  if (!e) return [];
  try {
    const res = await api.get<{ matches?: OwnerMatch[] }>(
      `/api/owners/lookup?email=${encodeURIComponent(e)}`,
    );
    return Array.isArray(res?.matches) ? res.matches : [];
  } catch {
    return [];
  }
}

/**
 * Is owner matching (OWNER_MATCH_ENABLED) on? One cheap probe per EPMA page
 * load — see ownerMatchAvailableFrom for how the answer is read. Never throws:
 * any failure is "off", which leaves the EPMA form exactly as it was.
 */
export async function probeOwnerMatch(): Promise<boolean> {
  try {
    const body = await api.get<unknown>('/api/owners/lookup?email=');
    return ownerMatchAvailableFrom({ status: 200, body });
  } catch (err) {
    return err instanceof ApiError ? ownerMatchAvailableFrom({ status: err.status, body: err.body }) : false;
  }
}

/**
 * Likely existing AppFolio owners for a NEW owner's typed name (§13.1) —
 * fuzzy: name order, middle names / initials, nicknames, couple listings,
 * company suffixes. At most 5, best first. [] on any failure or when off.
 */
export async function findSimilarOwners(q: {
  first?: string;
  last?: string;
  company?: string;
}): Promise<SimilarOwner[]> {
  const params = new URLSearchParams();
  for (const k of ['first', 'last', 'company'] as const) {
    const v = q[k]?.trim();
    if (v) params.set(k, v);
  }
  if ([...params.keys()].length === 0) return [];
  try {
    const res = await api.get<{ candidates?: SimilarOwner[] }>(`/api/owners/similar?${params.toString()}`);
    return Array.isArray(res?.candidates) ? res.candidates.slice(0, 5) : [];
  } catch {
    return [];
  }
}

/** Owners by name / email / company. Throws, so the picker can say the search failed. */
export async function searchOwners(q: string): Promise<OwnerMatch[]> {
  const res = await api.get<{ owners?: OwnerMatch[] }>(
    `/api/owners/search?q=${encodeURIComponent(q.trim())}`,
  );
  return Array.isArray(res?.owners) ? res.owners : [];
}

/** Vendors by name / company / email. Throws, so the picker can say the search failed. */
export async function searchVendors(q: string): Promise<VendorMatch[]> {
  const res = await api.get<{ vendors?: VendorMatch[] }>(
    `/api/vendors/search?q=${encodeURIComponent(q.trim())}`,
  );
  return Array.isArray(res?.vendors) ? res.vendors : [];
}

function bodyOf(err: ApiError): {
  error?: string;
  recipientName?: string;
  message?: string;
  field?: string;
} {
  return (err.body ?? {}) as { error?: string; recipientName?: string; message?: string; field?: string };
}

/** A server message is shown only if it carries no id (docs/no-ids-in-ui.md). */
function safeMessage(msg: string | undefined): string {
  const m = (msg ?? '').trim();
  return m && !OPAQUE_ID_RE.test(m) ? m : '';
}

/**
 * Send a standalone W-9 request. Every documented status is folded into a
 * W9RequestOutcome rather than thrown, so the page renders one notice per case.
 * Codes (spec §12 + routes/w9Requests.ts): 201 · 400 staff_email |
 * vendor_requires_id | unknown_recipient | invalid · 409 open_request_exists |
 * open_request_completed | request_in_flight · 502 cancel_failed | send_failed |
 * send_outcome_unknown · 503 disabled | directory_unavailable | template_unconfigured.
 */
export async function createW9Request(body: W9RequestBody): Promise<W9RequestOutcome> {
  try {
    const res = await api.post<{ status?: string; recipientName?: string; replacedOpen?: boolean }>(
      '/api/w9-requests',
      body,
    );
    return {
      kind: 'sent',
      recipientName: res?.recipientName?.trim() || body.displayName,
      replacedOpen: res?.replacedOpen === true,
    };
  } catch (err) {
    if (!(err instanceof ApiError)) {
      return {
        kind: 'failed',
        message: safeMessage(err instanceof Error ? err.message : undefined),
      };
    }
    const b = bodyOf(err);
    const recipientName = b.recipientName?.trim() || body.displayName;
    const message = safeMessage(b.message);
    if (err.status === 409) {
      if (b.error === 'open_request_exists') return { kind: 'open_request_exists', recipientName };
      if (b.error === 'open_request_completed') return { kind: 'open_request_completed', recipientName };
      if (b.error === 'request_in_flight') return { kind: 'in_flight', recipientName };
    }
    if (err.status === 400) {
      if (b.error === 'staff_email' || b.error === 'vendor_requires_id' || b.error === 'unknown_recipient') {
        return { kind: 'rejected', error: b.error };
      }
      return b.field ? { kind: 'rejected', error: 'invalid', field: b.field } : { kind: 'rejected', error: 'invalid' };
    }
    if (err.status === 503) {
      if (b.error === 'directory_unavailable' || b.error === 'template_unconfigured') {
        return message ? { kind: 'unavailable', message } : { kind: 'unavailable' };
      }
      return message ? { kind: 'not_enabled', message } : { kind: 'not_enabled' };
    }
    if (err.status === 502 && b.error === 'send_outcome_unknown') {
      return { kind: 'failed', message, outcomeUnknown: true };
    }
    return {
      kind: 'failed',
      // Never surface the machine code (b.error) itself — only the server's plain words.
      message: message || 'Try again in a moment.',
    };
  }
}

/**
 * The AppFolio sync ops for one engagement. Null when the endpoint is absent
 * (404) or switched off (503) — the Owners page hides the section then.
 */
export async function getOwnerSync(engagementGroupId: string): Promise<OwnerSyncView | null> {
  try {
    const res = await api.get<Partial<OwnerSyncView>>(
      `/api/owner-sync/${encodeURIComponent(engagementGroupId)}`,
    );
    return {
      ops: Array.isArray(res?.ops) ? res.ops : [],
      appfolioOwnerLinked: !!res?.appfolioOwnerLinked,
    };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 503)) return null;
    throw err;
  }
}

/** Start a new attempt for a failed / stuck op. */
export async function retryOwnerSyncOp(opKey: string): Promise<void> {
  await api.post('/api/owner-sync/ops/retry', { opKey });
}

/**
 * Send a stuck step again as a FRESH attempt, after staff checked AppFolio and
 * the change is not there (only offered when the view says canResendConfirmed —
 * the router's recorded outcome is final, so Retry would just replay it).
 * Refusals throw ApiError (409 { error: 'not_eligible', message }); the page
 * shows them through syncActionErrorMessage — words only, never a code or id.
 */
export async function resendOwnerSyncOpConfirmed(opKey: string): Promise<void> {
  await api.post('/api/owner-sync/ops/resend-confirmed', { opKey });
}

/**
 * Mark a failed / stuck op done by hand (optionally with a note); releases
 * dependents. `appfolioId` names the AppFolio owner a CREATE step made — the
 * server requires it for a create (409 owner_required otherwise) and writes it
 * back so the steps waiting on that owner can run. Request field only.
 */
export async function resolveOwnerSyncOp(
  opKey: string,
  note?: string,
  appfolioId?: string,
): Promise<void> {
  const n = note?.trim();
  const id = appfolioId?.trim();
  await api.post('/api/owner-sync/ops/resolve', {
    opKey,
    ...(id ? { appfolioId: id } : {}),
    ...(n ? { note: n } : {}),
  });
}

/** POST /api/owner-intake/:groupId/link-property's outcome, in the page's terms. */
export type LinkPropertyOutcome =
  | { kind: 'linked'; released: number }
  /** 409 — the addresses disagree; resend with `override` to link anyway. */
  | { kind: 'mismatch'; mismatch: LinkPropertyMismatch };

/**
 * Link the AppFolio property an onboarding became (city-utility plan §6.2). The
 * property id rides in the request only. Any failure other than the address
 * mismatch throws the ApiError (the caller shows syncActionErrorMessage).
 */
export async function linkOwnerIntakeProperty(
  groupId: string,
  propertyId: string,
  override = false,
): Promise<LinkPropertyOutcome> {
  try {
    const res = await api.post<{ ok?: boolean; released?: number }>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/link-property`,
      { propertyId, ...(override ? { override: true } : {}) },
    );
    return { kind: 'linked', released: typeof res?.released === 'number' ? res.released : 0 };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const mismatch = linkPropertyMismatchFrom(err.body);
      if (mismatch) return { kind: 'mismatch', mismatch };
    }
    throw err;
  }
}
